import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationSound] = useState(new Audio('/notification.mp3'));

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'new_message':
        const message = data.message;
        
        // Add message to state
        setMessages(prev => ({
          ...prev,
          [message.conversation_id]: [
            ...(prev[message.conversation_id] || []),
            message
          ]
        }));
        
        // Play notification sound if not from current user
        if (message.sender_id !== user?.id) {
          try {
            notificationSound.play().catch(e => console.log('Sound play failed:', e));
          } catch (error) {
            console.log('Notification sound error:', error);
          }
          setUnreadCount(prev => prev + 1);
        }
        break;
        
      case 'typing':
        setTypingUsers(prev => ({
          ...prev,
          [data.conversation_id]: data.is_typing ? [...new Set([...(prev[data.conversation_id] || []), data.user_id])] : (prev[data.conversation_id] || []).filter(id => id !== data.user_id)
        }));
        break;
        
      case 'read_receipt':
        setMessages(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(convId => {
            updated[convId] = updated[convId].map(msg =>
              msg.id === data.message_id ? { ...msg, is_read: true, read_at: data.read_at } : msg
            );
          });
          return updated;
        });
        break;
        
      case 'notification_update':
        setUnreadCount(data.unread_messages);
        break;
        
      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  }, [user?.id, notificationSound]);

  const { isConnected, sendTypingIndicator, markMessageRead } = useWebSocket(handleWebSocketMessage);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/realtime/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token]);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/realtime/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => ({
        ...prev,
        [conversationId]: response.data.messages || []
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [token]);

  const sendMessage = useCallback(async (receiverId, content, emoji = null) => {
    if (!token) return;
    
    try {
      await axios.post(
        `${API_URL}/api/realtime/messages/send`,
        { receiver_id: receiverId, content, emoji },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [token]);

  const sendVoiceMessage = useCallback(async (receiverId, audioBlob) => {
    if (!token) return;
    
    try {
      const formData = new FormData();
      formData.append('voice_file', audioBlob, 'voice_message.webm');
      
      await axios.post(
        `${API_URL}/api/realtime/messages/voice?receiver_id=${receiverId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
    } catch (error) {
      console.error('Error sending voice message:', error);
      throw error;
    }
  }, [token]);

  const clearNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      await axios.post(
        `${API_URL}/api/realtime/notifications/clear`,
        { notification_type: 'messages' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchConversations();
    }
  }, [token, fetchConversations]);

  const value = {
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    typingUsers,
    unreadCount,
    isConnected,
    fetchConversations,
    fetchMessages,
    sendMessage,
    sendVoiceMessage,
    sendTypingIndicator,
    markMessageRead,
    clearNotifications
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
